// Hideo - saving the active profile's data (saveAppData / persist): this browser (or the legacy data folder), the
// "changes not saved yet" mark and the online autosave right after. Also the data of any profile (profileData,
// writeProfileData) and the content of a profile file (profileFileText), used by the floppy-disk file and the online save.
// The data itself (profiles list, active profile's data, normalizeData) is src/stores/data.ts and src/data.
// Ported from the legacy core/data.js (verbatim copy in tests/fixtures/legacy/data.js, golden tests in tests/saving*.test.ts).
import { t } from '../../i18n'
import { dataFile, lsKey, SOCIALS } from '../../data/profiles'
import { normalizeData } from '../../data/normalize'
import type { ProfileData } from '../../data/types'
import { useDataStore } from '../../stores/data'
import { uiAlert } from '../../stores/notices'
import { FOLDER, readFolderJson, writeFile, writeFolder, type FolderHandle } from './folder'
import { fileStamp, setSync, syncOf } from './sync'
import { queueCloudSync } from '../cloud/autosync'
import { mediaInline } from '../cloud/media'
import { app } from '../app'

/** Grows at every change: this is how the online save knows whether something changed while the file was travelling (drive.ts) */
let editRev = 0
export const editRevNow = (): number => editRev

/** Saves the active profile's data (browser + online sync) and redraws the pages (noRender: only saves, e.g. the
 *  editor's autosave) */
export function saveAppData(data: object, noRender?: boolean): void { useDataStore().setData(data); persist(); if (!noRender) app.renderAll() }
/** Writes the active profile's data in memory to this browser (or to the legacy data folder), marks it to save, and
 *  queues the online save */
export function persist(): void {
  const store = useDataStore()
  editRev++
  if (FOLDER.ok) { clearTimeout(FOLDER.writeTimer as ReturnType<typeof setTimeout>); FOLDER.writeTimer = setTimeout(writeFolder, 500) }
  else {
    try { store.writeCurrentLocal() }
    catch { uiAlert(t('Browser storage is full: the data of this profile was not saved in the browser. Save it to Google Drive or to a file.')) }
  }
  if (!syncOf(store.profiles.current).dirty) setSync(store.profiles.current, { dirty: true })
  queueCloudSync()   // online save right after (autosync.ts)
}

/** Data of any profile: the active one is in memory, the others in the folder or in the browser */
export async function profileData(id: string): Promise<ProfileData> {
  const store = useDataStore()
  if (id === store.profiles.current) return store.getData()
  const d = FOLDER.ok ? await readFolderJson(dataFile(id)) : null
  return d ? normalizeData(d) : store.readProfileLocal(id)
}
/** Writes the data of any profile (the active one goes through saveAppData) */
export async function writeProfileData(id: string, data: ProfileData): Promise<void> {
  if (id === useDataStore().profiles.current) return saveAppData(data)
  if (FOLDER.ok) { await writeFile(FOLDER.dirHandle as FolderHandle, dataFile(id), JSON.stringify({ ...data, ...fileStamp() }, null, 1)); try { localStorage.removeItem(lsKey(id)) } catch { /* blocked storage */ } }
  else localStorage.setItem(lsKey(id), JSON.stringify(data))
}
/** Content of a profile file: data + profile information (name, photo, bio, socials) + date/device.
 *  Used by the floppy-disk button and by the online save. Online, images and voice notes are separate files (the text only
 *  has "media:<id>" + data.media); in the floppy-disk file (inline) they are embedded in Base64, so the file is a complete copy */
export async function profileFileText(id: string, stamp: { savedAt: string; savedOn: string }, inline = false): Promise<string> {
  const p = useDataStore().profiles.list.find(x => x.id === id)!
  const meta: Record<string, unknown> = { name: p.name, bio: p.bio, avatar: p.avatar }; SOCIALS.forEach(k => { meta[k] = p[k] })
  let d = await profileData(id)
  if (inline) d = await mediaInline(JSON.parse(JSON.stringify(d)))
  return JSON.stringify({ ...d, profile: meta, ...stamp }, null, 1)
}
